import { useCallback, useEffect, useRef } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { v4 as uuid } from "uuid";
import cartAtom, { createEmptyCart } from "@/atoms/cart";
import { lastSyncedCartAtom } from "@/atoms/last-synced-cart";
import categoriesAtom from "@/atoms/categories";
import {
  getSyncEngine,
  getOperationQueue,
  isAddPayload,
} from "@/lib/cart-sync";
import type {
  CartOperation,
  AddPayload,
  UpdatePayload,
  RemovePayload,
  CartSyncEngine,
  OperationQueue,
} from "@/lib/cart-sync";
import { CartItem } from "@/types";
import { trackMetaAddToCart } from "@/lib/meta-pixel";
import { recordStorefrontCartCleared, recordStorefrontEvent } from "@/domain/storefront-analytics";
import { useAuthStatus } from "./use-auth-status";
import { cartBackendService } from "@/lib/cart-backend-service";
import { clearLocalCart, saveLocalCart, touchCartTimestamp } from "@/lib/cart-local-storage";
import { findCategoryForOffer } from "@/lib/find-category-for-offer";
import { sumCartItems } from "@/lib/pricing";
import { resetCartInitialized } from "./use-cart";
import { refreshApiData } from "./use-api-polling";
import { cartSyncEnabled } from "@/consts";

import { atom } from "jotai";

/** Integer cents for the displayed unit price (base offer + chosen options), the wire shape `add_to_cart`/`remove_from_cart` require. */
function unitPriceCentsOf(cartItem: Pick<CartItem, "unitPrice">): number {
  return Math.round(cartItem.unitPrice * 100);
}

export const cartSyncStatusAtom = atom({
  isSyncing: false,
  pendingCount: 0,
  lastError: null as string | null,
});

export function useOptimisticCart() {
  const [cart, setCart] = useAtom(cartAtom);
  const setLastSyncedCart = useSetAtom(lastSyncedCartAtom);
  const categories = useAtomValue(categoriesAtom);
  const isAuthenticated = useAuthStatus();
  const [syncStatus, setSyncStatus] = useAtom(cartSyncStatusAtom);
  const shouldUseRemoteCart = cartSyncEnabled && isAuthenticated;

  const engineRef = useRef<CartSyncEngine | null>(null);
  const queueRef = useRef<OperationQueue | null>(null);

  const getEngine = useCallback(() => {
    if (!engineRef.current) {
      engineRef.current = getSyncEngine();
    }
    return engineRef.current;
  }, []);

  const getQueue = useCallback(() => {
    if (!queueRef.current) {
      queueRef.current = getOperationQueue();
    }
    return queueRef.current;
  }, []);

  const previousStatesRef = useRef<Map<string, CartItem | null>>(new Map());

  const pendingEnqueuesRef = useRef<Set<Promise<void>>>(new Set());

  useEffect(() => {
    if (!shouldUseRemoteCart) {
      setSyncStatus({ isSyncing: false, pendingCount: 0, lastError: null });
      return;
    }

    const engine = getEngine();
    const queue = getQueue();

    engine.onSyncStart = () => {
      setSyncStatus((prev) => ({ ...prev, isSyncing: true }));
    };

    engine.onSyncComplete = (serverIdMap) => {
      if (serverIdMap.size > 0) {
        setCart((prev) => ({
          ...prev,
          items: prev.items.map((item) => {
            const serverId = serverIdMap.get(item.id);
            return serverId ? { ...item, id: serverId } : item;
          }),
        }));

        setLastSyncedCart((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            items: prev.items.map((item) => {
              const serverId = serverIdMap.get(item.id);
              return serverId ? { ...item, id: serverId } : item;
            }),
          };
        });
      }

      setSyncStatus((prev) => ({
        ...prev,
        isSyncing: false,
        lastError: null,
      }));

      queue.getPendingCount().then((count) => {
        setSyncStatus((prev) => ({ ...prev, pendingCount: count }));
      });
    };

    engine.onSyncError = (error) => {
      setSyncStatus((prev) => ({
        ...prev,
        isSyncing: false,
        lastError: error.userMessage,
      }));
    };

    engine.onCrossTabUpdate = async () => {
      try {
        const serverCart = await cartBackendService.loadCart();
        if (serverCart) {
          setCart(serverCart);
          setLastSyncedCart(serverCart);
        }
      } catch (error) {
        console.error("[useOptimisticCart] Failed to reload cart:", error);
      }
    };

    const unsubscribe = queue.subscribe(async () => {
      const count = await queue.getPendingCount();
      setSyncStatus((prev) => ({ ...prev, pendingCount: count }));
    });

    return () => {
      unsubscribe();
    };
  }, [shouldUseRemoteCart, getEngine, getQueue, setCart, setLastSyncedCart, setSyncStatus]);

  const saveToLocalStorage = useCallback((cartData: { items: CartItem[] }) => {
    try {
      saveLocalCart(cartData);
    } catch (error) {
      console.error("[useOptimisticCart] Failed to save to localStorage:", error);
    }
  }, []);

  const addItem = useCallback(
    (cartItem: CartItem): string => {
      trackMetaAddToCart(cartItem);
      recordStorefrontEvent({
        type: "add_to_cart",
        itemId: cartItem.item.id,
        itemName: cartItem.item.name,
        unitPriceCents: unitPriceCentsOf(cartItem),
        quantity: Math.min(99, Math.max(1, cartItem.quantity)),
      });

      if (!shouldUseRemoteCart) {
        setCart((prev) => {
          const items = [...prev.items, cartItem];
          const newCart = { ...prev, items, totalPrice: sumCartItems(items) };
          saveToLocalStorage(newCart);
          return newCart;
        });
        return cartItem.id;
      }

      touchCartTimestamp();

      const tempId = uuid();
      const itemWithTempId = { ...cartItem, id: tempId };

      setCart((prev) => {
        const items = [...prev.items, itemWithTempId];
        return { ...prev, items, totalPrice: sumCartItems(items) };
      });

      previousStatesRef.current.set(tempId, null);

      const categoryId = findCategoryForOffer(categories, cartItem.itemOffer.id)?.id || "";

      const operation: CartOperation = {
        id: uuid(),
        type: "ADD",
        timestamp: Date.now(),
        idempotencyKey: `add-${tempId}`,
        payload: {
          tempId,
          item: {
            itemOfferId: cartItem.itemOffer.id,
            categoryId,
            quantity: cartItem.quantity,
            unitPrice: cartItem.unitPrice,
            itemTotalPrice: cartItem.itemTotalPrice,
            specialInstructions: cartItem.specialInstructions,
            options: Object.entries(cartItem.optionQuantities).flatMap(
              ([optionGroupId, quantities]) =>
                Object.entries(quantities)
                  .filter(([_, qty]) => qty > 0)
                  .map(([optionId, quantity]) => ({
                    optionGroupId,
                    optionId,
                    quantity,
                    specialInstructions:
                      cartItem.optionSpecialInstructions?.[optionGroupId]?.[optionId] ?? "",
                  }))
            ),
            item: {
              id: cartItem.item.id,
              name: cartItem.item.name,
              image: cartItem.item.image,
            },
            itemOffer: {
              id: cartItem.itemOffer.id,
              price: cartItem.itemOffer.price,
            },
            optionGroups: cartItem.optionGroups.map((og) => ({
              id: og.id,
              name: og.name,
              options: og.options.map((opt) => ({ id: opt.id, name: opt.itemId })),
            })),
            itemOptions: Object.fromEntries(
              Object.entries(cartItem.itemOptions).map(([key, item]) => [
                key,
                { id: item.id, name: item.name },
              ])
            ),
            optionQuantities: cartItem.optionQuantities,
          },
        } as AddPayload,
        status: "PENDING",
        retryCount: 0,
      };

      const enqueuePromise = getQueue().enqueue(operation);
      pendingEnqueuesRef.current.add(enqueuePromise);
      enqueuePromise.finally(() => {
        pendingEnqueuesRef.current.delete(enqueuePromise);
      });

      return tempId;
    },
    [shouldUseRemoteCart, setCart, categories, saveToLocalStorage, getQueue]
  );

  const removeItem = useCallback(
    (itemId: string) => {
      const existingItem = cart.items.find((i) => i.id === itemId);
      if (!existingItem) return;

      recordStorefrontEvent({
        type: "remove_from_cart",
        itemId: existingItem.item.id,
        itemName: existingItem.item.name,
        unitPriceCents: unitPriceCentsOf(existingItem),
        quantity: Math.min(99, Math.max(1, existingItem.quantity)),
      });

      setCart((prev) => {
        const items = prev.items.filter((item) => item.id !== itemId);
        const newCart = { ...prev, items, totalPrice: sumCartItems(items) };
        if (!shouldUseRemoteCart) {
          saveToLocalStorage(newCart);
        }
        return newCart;
      });

      if (!shouldUseRemoteCart) return;

      touchCartTimestamp();
      previousStatesRef.current.set(itemId, existingItem);

      const operation: CartOperation = {
        id: uuid(),
        type: "REMOVE",
        timestamp: Date.now(),
        idempotencyKey: `remove-${itemId}`,
        payload: {
          itemId,
        } as RemovePayload,
        status: "PENDING",
        retryCount: 0,
        previousState: existingItem,
      };

      const enqueuePromise = getQueue().enqueue(operation);
      pendingEnqueuesRef.current.add(enqueuePromise);
      enqueuePromise.finally(() => {
        pendingEnqueuesRef.current.delete(enqueuePromise);
      });
      enqueuePromise.then(() => getEngine().flush());
    },
    [cart.items, shouldUseRemoteCart, setCart, saveToLocalStorage, getQueue, getEngine]
  );

  const updateItemQuantity = useCallback(
    (itemId: string, quantity: number) => {
      const existingItem = cart.items.find((i) => i.id === itemId);
      if (!existingItem) return;

      if (quantity === 0) {
        removeItem(itemId);
        return;
      }

      const delta = quantity - existingItem.quantity;
      if (delta > 0) {
        recordStorefrontEvent({
          type: "add_to_cart",
          itemId: existingItem.item.id,
          itemName: existingItem.item.name,
          unitPriceCents: unitPriceCentsOf(existingItem),
          quantity: Math.min(99, delta),
        });
      } else if (delta < 0) {
        recordStorefrontEvent({
          type: "remove_from_cart",
          itemId: existingItem.item.id,
          itemName: existingItem.item.name,
          unitPriceCents: unitPriceCentsOf(existingItem),
          quantity: Math.min(99, -delta),
        });
      }

      setCart((prev) => {
        const items = prev.items.map((item) =>
          item.id === itemId
            ? { ...item, quantity, itemTotalPrice: item.unitPrice * quantity }
            : item
        );
        const newCart = { ...prev, items, totalPrice: sumCartItems(items) };
        if (!shouldUseRemoteCart) {
          saveToLocalStorage(newCart);
        }
        return newCart;
      });

      if (!shouldUseRemoteCart) return;

      touchCartTimestamp();
      previousStatesRef.current.set(itemId, existingItem);

      const operation: CartOperation = {
        id: uuid(),
        type: "UPDATE",
        timestamp: Date.now(),
        idempotencyKey: `update-${itemId}-${Date.now()}`,
        payload: {
          itemId,
          changes: { quantity },
        } as UpdatePayload,
        status: "PENDING",
        retryCount: 0,
        previousState: existingItem,
      };

      const enqueuePromise = getQueue().enqueue(operation);
      pendingEnqueuesRef.current.add(enqueuePromise);
      enqueuePromise.finally(() => {
        pendingEnqueuesRef.current.delete(enqueuePromise);
      });

    },
    [cart.items, shouldUseRemoteCart, setCart, removeItem, saveToLocalStorage, getQueue]
  );

  const clearCart = useCallback(() => {
    recordStorefrontCartCleared("manual");
    const previousCart = cart;

    setCart(createEmptyCart());
    refreshApiData();

    if (!shouldUseRemoteCart) {
      saveToLocalStorage(createEmptyCart());
      return;
    }

    const operation: CartOperation = {
      id: uuid(),
      type: "CLEAR",
      timestamp: Date.now(),
      idempotencyKey: `clear-${Date.now()}`,
      payload: {},
      status: "PENDING",
      retryCount: 0,
      previousState: previousCart,
    };

    const enqueuePromise = getQueue().enqueue(operation);
    pendingEnqueuesRef.current.add(enqueuePromise);
    enqueuePromise.finally(() => {
      pendingEnqueuesRef.current.delete(enqueuePromise);
    });
    enqueuePromise.then(() => getEngine().flush());
  }, [cart, shouldUseRemoteCart, setCart, saveToLocalStorage, getQueue, getEngine]);

  const clearAfterCheckout = useCallback(async () => {
    setCart(createEmptyCart());
    setLastSyncedCart(null);
    refreshApiData();

    clearLocalCart();

    resetCartInitialized();

    if (queueRef.current) {
      queueRef.current.clear().catch((error) => {
        console.error("[clearAfterCheckout] Failed to clear queue:", error);
      });
    }

    if (shouldUseRemoteCart) {
      cartBackendService.clearCart().catch((error) => {
        console.error("[clearAfterCheckout] Failed to clear server cart:", error);
      });
    }
  }, [setCart, setLastSyncedCart, shouldUseRemoteCart]);

  const rollback = useCallback(
    (operationId: string, operation: CartOperation) => {
      if (operation.type === "ADD" && isAddPayload(operation.payload)) {
        const addPayload = operation.payload as AddPayload;
        setCart((prev) => ({
          ...prev,
          items: prev.items.filter((i) => i.id !== addPayload.tempId),
        }));
      } else if (operation.previousState) {
        const prevItem = operation.previousState as CartItem;

        setCart((prev) => {
          const existingIdx = prev.items.findIndex((i) => i.id === prevItem.id);

          if (existingIdx >= 0) {
            const newItems = [...prev.items];
            newItems[existingIdx] = prevItem;
            return { ...prev, items: newItems };
          } else {
            return { ...prev, items: [...prev.items, prevItem] };
          }
        });
      }

      previousStatesRef.current.delete(operationId);
    },
    [setCart]
  );

  const flush = useCallback(async () => {
    if (!shouldUseRemoteCart) return;

    if (pendingEnqueuesRef.current.size > 0) {
      await Promise.all(pendingEnqueuesRef.current);
    }
    await getEngine().flush();
  }, [shouldUseRemoteCart, getEngine]);

  const getPendingCount = useCallback(async () => {
    if (!shouldUseRemoteCart) return 0;
    return getQueue().getPendingCount();
  }, [shouldUseRemoteCart, getQueue]);

  return {
    cart,
    syncStatus,
    addItem,
    updateItemQuantity,
    removeItem,
    clearCart,
    clearAfterCheckout,
    rollback,
    flush,
    getPendingCount,
    hasPendingChanges: shouldUseRemoteCart && syncStatus.pendingCount > 0,
    isSyncing: shouldUseRemoteCart && syncStatus.isSyncing,
  };
}

export { flushCartSync } from "./use-cart-sync";
